import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { searchPath } from "@outschool/routes";
import { useSessionStorageState } from "@outschool/session-storage";
import { dayjs } from "@outschool/time";
import { getEsaWaitPeriod, useSession } from "@outschool/ui-auth";
import _ from "lodash";
import React, { useCallback, useContext } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { ESA_APPROVED_TAG_PREFIX } from "../../shared/Esa";
import * as SearchFilters from "../../shared/SearchFilters";
import * as User from "../../shared/User";
import useExperiment from "../hooks/useExperiment";
import useTrackSearchFiltersApplied from "../hooks/useTrackSearchFiltersApplied";

const AGE_FILTER_LOCAL_STORAGE_KEY = "ageFilter";

/**
 * Type extended for filters used in the UI but not sent to the backend
 * (userName is always paired with userUid, but allows us to reconstruct
 * the UI)
 */
export type ExtendedSearchFiltersType = SearchFiltersType & {
  userName?: string;
};

export type SetFiltersType = (
  category: string,
  partialFilters: ExtendedSearchFiltersType,
  options?: { overrideRouting?: boolean }
) => void;

interface SearchFiltersContextValue {
  searchUid?: string;
  setFilters?: SetFiltersType;
  filters?: ExtendedSearchFiltersType;
  clearSearchInputBox?: () => void;
  clearSearchInputBoxRef?: React.RefObject<HTMLInputElement>;
}

export const SearchFiltersContext =
  React.createContext<SearchFiltersContextValue>({});

const clearSearchInputBoxRef = React.createRef<HTMLInputElement>();

export function SearchFiltersContextProvider({
  children
}: React.PropsWithChildren<{}>) {
  const { currentUser, esaSessionData } = useSession();
  const esaWaitPeriod = getEsaWaitPeriod();

  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  const esaState = esaSessionData?.shipping?.state;
  const [persistedAdminTagFilters, setPersistedAdminTagFilters] =
    React.useState(!!esaState ? [`${ESA_APPROVED_TAG_PREFIX}${esaState}`] : []);

  const defaultLearnerAges =
    User.preferredAges(currentUser).length > 0
      ? User.preferredAges(currentUser).join(",")
      : undefined;

  const numberOfLearners = currentUser?.children?.length ?? 0;

  const noAutoAgeFilter = numberOfLearners > 1;

  const [persistedAgeFilter, setPersistedAgeFilter] =
    useSessionStorageState<string>(
      AGE_FILTER_LOCAL_STORAGE_KEY,
      noAutoAgeFilter ? undefined : defaultLearnerAges
    );

  const { searchUid, trackSearchFiltersApplied } =
    useTrackSearchFiltersApplied();

  /* Filters priority (from lowest to highest priority):
      - age from local storage (more info here: https://outschool.slack.com/archives/C016RTZPMQ9/p1648835968536299)
      - filters from query string
  */
  const filters = React.useMemo(() => {
    const filtersFromQuery = SearchFilters.fromQueryAndParams(
      // @ts-ignore TS(2339): Property 'query' does not exist on type 'Location<... Remove this comment to see the full error message
      location.query,
      params
    );
    const esaSearchDate = {
      startAfter: dayjs().add(esaWaitPeriod, "days").format("YYYY-MM-DD")
    };
    const defaultScheduleFiltersToUse = esaState ? esaSearchDate : {};
    const _filters = {
      ...defaultScheduleFiltersToUse,
      age:
        persistedAgeFilter
          ?.split(",")
          ?.map(age => parseInt(age))
          ?.filter(age => age < 19)
          ?.join(",") ?? persistedAgeFilter,
      adminTags: persistedAdminTagFilters?.join(","),
      ...filtersFromQuery
    };

    if (_filters?.age) {
      const age = _filters.age
        ?.split(",")
        ?.map(age => parseInt(age))
        ?.filter(age => age < 19)
        ?.join(",");

      if (age === "") {
        delete _filters["age"];
      } else {
        _filters.age = age;
      }
    }

    if (!!_filters?.adminTags?.trim()) {
      _filters.adminTags = _filters.adminTags.split(",");
    } else {
      _filters.adminTags = undefined;
    }

    if (_filters?.theme) {
      _filters.theme = SearchFilters.convertThemeQueryToFilter(_filters.theme);
    }
    return _filters;
  }, [
    persistedAgeFilter,
    persistedAdminTagFilters,
    // @ts-ignore TS(2339): Property 'query' does not exist on type 'Location<... Remove this comment to see the full error message
    location.query,
    params,
    esaState,
    esaWaitPeriod
  ]);

  const setFilters = useCallback<SetFiltersType>(
    (filterCategory, partialFilters, options) => {
      if ("q" in partialFilters && "multiTermQuery" in filters) {
        delete filters.multiTermQuery;
      }

      if ("multiTermQuery" in partialFilters && "q" in filters) {
        delete filters.q;
      }
      const newFilters: SearchFiltersType = _.omitBy(
        SearchFilters.omitCatchalls({
          ...filters,
          ...partialFilters
        }),
        _.isNil
      );

      const oldFilters = _.omitBy(
        SearchFilters.omitCatchalls(filters),
        _.isNil
      );

      trackSearchFiltersApplied({ filterCategory, newFilters, oldFilters });

      if (!_.isEqual(oldFilters, newFilters)) {
        setPersistedAgeFilter(partialFilters.age);
        // @ts-ignore TS(2345): Argument of type 'InputMaybe<string[]> | undefined... Remove this comment to see the full error message
        setPersistedAdminTagFilters(newFilters.adminTags);
      }

      let newFiltersWithFormattedTheme = Object.assign({}, newFilters);
      if (newFilters.theme) {
        newFiltersWithFormattedTheme.theme =
          SearchFilters.convertThemeFilterToQuery(newFilters.theme);
      }

      if (!options?.overrideRouting) {
        history.push(searchPath(newFiltersWithFormattedTheme), {
          filterCategory
        });
      }
    },
    [history, filters, setPersistedAgeFilter, trackSearchFiltersApplied]
  );

  const clearSearchInputBox = useCallback(() => {
    clearSearchInputBoxRef.current?.click();
  }, []);

  const value = React.useMemo(
    () => ({
      clearSearchInputBox,
      clearSearchInputBoxRef,
      filters,
      setFilters,
      searchUid
    }),
    [clearSearchInputBox, filters, setFilters, searchUid]
  );

  const { trigger } = useExperiment("GradeLevelSearchFilter");

  React.useEffect(() => {
    if (location.pathname === searchPath()) {
      trigger({
        additionalProperties: {
          filters: filters,
          searchUid: searchUid
        }
      });
    }
  }, [trigger, searchUid, filters, location]);

  return (
    <SearchFiltersContext.Provider value={value}>
      {children}
    </SearchFiltersContext.Provider>
  );
}

export function useSearchFilters() {
  const context = useContext(SearchFiltersContext);
  if (!context.setFilters) {
    throw new Error("Called useSearchFilters without a SearchFiltersContext");
  }
  return context;
}
